import React from 'react';
import { useContent } from '../../../contexts/ContentContext';
import { useEditMode } from '../../../contexts/EditModeContext';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit';
import { useTranslations } from '../../../contexts/TranslationContext';
import { useGameType } from '../../../contexts/GameTypeContext';
import { useSiteConfig } from '../../../contexts/SiteConfigContext';
import { usePageSections } from '../../../hooks/usePageSections';
import { resolveCtaTargetCard } from '../../../utils/ctaHelpers';
import {
  HowToPlayTitle,
  HowToPlayHeroTagline,
  HowToPlayHeroSubtitle,
} from '../../../content/keys';

const FALLBACKS = {
  [HowToPlayHeroTagline]: 'YOUR GUIDE TO WINNING',
  [HowToPlayTitle]: 'How to Play',
  [HowToPlayHeroSubtitle]: 'Follow these simple steps to get started and join the excitement!',
};

const SUIT_TO_KEY = { '♠': 'spades', '♥': 'hearts', '♦': 'diamonds', '♣': 'clubs' };
const RANK_FALLBACKS = { A: 'Ace', K: 'King', Q: 'Queen', J: 'Jack' };
const SUIT_OF_FALLBACKS = { spades: 'of Spades', hearts: 'of Hearts', diamonds: 'of Diamonds', clubs: 'of Clubs' };

export default function HowToHeroSection({ section }) {
  const { c } = useContent();
  const { editMode, handleTranslationUpdate } = useEditMode();
  const { t: tr } = useTranslations();
  const { gameType } = useGameType();
  const { siteDetails } = useSiteConfig();
  // Load the CTA home page sections to get the cards_remaining config (which
  // holds the real targetCard). This is the same source ChaseTheAceStyleStandard
  // uses. Cached in localStorage so it's fast for returning visitors.
  const { sections: homeSections } = usePageSections('chasetheace_home');
  // Use tr() only when it returns a real translation, not the key name itself
  const t = (key) => { const v = tr(key); return c(key, (v !== key ? v : null) || FALLBACKS[key] || ''); };

  const isChaseTheAce = gameType === 'chaseTheAce';

  // Resolve actual target card from the CTA *home* page sections (cards_remaining
  // config holds the real targetCard). Falling back to siteDetails fields if set.
  const ctaTarget = isChaseTheAce ? resolveCtaTargetCard(homeSections, siteDetails) : null;
  const isAceOfSpades = ctaTarget?.rank === 'A' && ctaTarget?.suit === '♠';

  // Build a localized card name using translated rank + suit-of strings
  const localizedTargetCard = (() => {
    if (!ctaTarget) return '';
    const { rank, suit } = ctaTarget;
    const suitKey = SUIT_TO_KEY[suit] ?? suit;
    const rankName = tr(`chaseTheAce.cardRankNames.${rank}`) || RANK_FALLBACKS[rank] || rank;
    if (isAceOfSpades) return rankName; // "Ace" / "l'As" / "el As" — no suit needed
    const suitOf = tr(`chaseTheAce.cardSuitOf.${suitKey}`) || SUIT_OF_FALLBACKS[suitKey] || `of ${suit}`;
    return `${rankName} ${suitOf}`;
  })();

  // For CTA: interpolate {{targetCard}} into the translated title/subtitle templates
  const ctaTitle = isChaseTheAce
    ? (tr('chaseTheAce.howItWorksTitle') || 'How to Play Chase the {{targetCard}}').replace('{{targetCard}}', localizedTargetCard)
    : null;

  const ctaSubtitle = isChaseTheAce
    ? (tr('chaseTheAce.howItWorksDesc') || 'A thrilling card game where {{targetCard}} holds the jackpot \u2014 be the one to find it!').replace('{{targetCard}}', localizedTargetCard)
    : null;

  return (
    <div
      className="relative py-10 xs:py-14 sm:py-20 px-3 sm:px-6 lg:px-8 overflow-hidden rounded-2xl mb-6 sm:mb-12 md:mb-16"
      style={{ background: 'linear-gradient(135deg, var(--charity-primary, #1e40af), var(--charity-primary-dark, #1e3a8a))' }}
    >
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full animate-float-slow"></div>
        <div className="absolute top-40 right-32 w-48 h-48 bg-white rounded-full animate-float-medium"></div>
        <div className="absolute bottom-32 left-1/3 w-56 h-56 bg-white rounded-full animate-float-fast"></div>
      </div>

      <div className="max-w-4xl mx-auto text-center relative z-10">
        <InlineTranslationEdit
          translationKey={HowToPlayHeroTagline}
          value={t(HowToPlayHeroTagline)}
          fallback={FALLBACKS[HowToPlayHeroTagline]}
          editMode={editMode}
          onUpdate={handleTranslationUpdate}
          className="text-white/80 text-xs sm:text-lg font-semibold mb-1.5 sm:mb-3 tracking-wider block"
        />
        <InlineTranslationEdit
          translationKey={HowToPlayTitle}
          value={c(HowToPlayTitle, ctaTitle ?? FALLBACKS[HowToPlayTitle])}
          fallback={FALLBACKS[HowToPlayTitle]}
          editMode={editMode}
          onUpdate={handleTranslationUpdate}
          className="text-2xl xs:text-3xl sm:text-5xl md:text-6xl font-bold text-white mb-3 sm:mb-6 drop-shadow-lg block leading-tight"
        />
        <InlineTranslationEdit
          translationKey={HowToPlayHeroSubtitle}
          value={c(HowToPlayHeroSubtitle, ctaSubtitle ?? FALLBACKS[HowToPlayHeroSubtitle])}
          fallback={FALLBACKS[HowToPlayHeroSubtitle]}
          editMode={editMode}
          onUpdate={handleTranslationUpdate}
          className="text-xs xs:text-sm sm:text-xl text-white/90 max-w-2xl mx-auto block leading-relaxed"
        />
      </div>
    </div>
  );
}
